import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Stack,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import {
  CalendarToday as CalendarIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { getLocalDateString } from '../../utils/dateUtils';

const DateRangeCalendar = ({ startDate, endDate, onChange, onClose }) => {
  const theme = useTheme();
  const [selectedQuickRange, setSelectedQuickRange] = useState(null);
  
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedStartDate, setSelectedStartDate] = useState(startDate);
  const [selectedEndDate, setSelectedEndDate] = useState(endDate);
  const [isSelectingEndDate, setIsSelectingEndDate] = useState(false);

  const quickRanges = [
    { label: 'Today', value: 'today' },
    { label: 'Tomorrow', value: 'tomorrow' },
    { label: 'This Week', value: 'thisWeek' },
    { label: 'This Month', value: 'thisMonth' },
    { label: 'This Year', value: 'thisYear' }
  ];

  // Format date for display in input
  const formatDateForInput = (date) => {
    if (!date) return '';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  // Format date for internal use (YYYY-MM-DD) - using local timezone
  const formatDateInternal = (date) => {
    if (!date) return '';
    return getLocalDateString(date);
  };

  // Get calendar days for a month (only current month dates)
  const getCalendarDays = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    const firstDayOfWeek = firstDay.getDay();
    const mondayOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // Monday = 0
    
    // Add empty slots for previous month days
    for (let i = 0; i < mondayOffset; i++) {
      days.push(null);
    }
    
    // Add all days of the current month
    for (let day = 1; day <= lastDay.getDate(); day++) {
      days.push(new Date(year, month, day));
    }
    
    // Add empty cells to complete the grid (6 rows × 7 days = 42 cells)
    while (days.length < 42) {
      days.push(null);
    }
    
    return days;
  };

  // Check if date is in current month
  const isCurrentMonth = (date, monthDate) => {
    return date.getMonth() === monthDate.getMonth() && 
           date.getFullYear() === monthDate.getFullYear();
  };

  // Check if date is selected
  const isDateSelected = (date) => {
    if (!selectedStartDate) return false;
    
    if (!selectedEndDate || selectedStartDate.getTime() === selectedEndDate.getTime()) {
      return date.toDateString() === selectedStartDate.toDateString();
    }
    
    return date >= selectedStartDate && date <= selectedEndDate;
  };

  // Check if date is in range (between start and end, but not start or end)
  const isInRange = (date) => {
    if (!selectedStartDate || !selectedEndDate) return false;
    if (selectedStartDate.getTime() === selectedEndDate.getTime()) return false;
    return date > selectedStartDate && date < selectedEndDate;
  };

  // Check if date is start or end date
  const isStartDate = (date) => {
    return selectedStartDate && date.toDateString() === selectedStartDate.toDateString();
  };

  const isEndDate = (date) => {
    return selectedEndDate && date.toDateString() === selectedEndDate.toDateString();
  };

  // Handle date click
  const handleDateClick = (date) => {
    setSelectedQuickRange(null); // Clear quick range selection when manually selecting dates
    
    if (!selectedStartDate || (selectedStartDate && selectedEndDate && !isSelectingEndDate)) {
      // Start new selection - use same date for both start and end
      setSelectedStartDate(date);
      setSelectedEndDate(date);
      setIsSelectingEndDate(true);
    } else if (isSelectingEndDate) {
      // Select end date
      if (date >= selectedStartDate) {
        setSelectedEndDate(date);
        setIsSelectingEndDate(false);
      } else {
        // If selected date is before start date, make it the new start date
        setSelectedStartDate(date);
        setSelectedEndDate(date);
        setIsSelectingEndDate(true);
      }
    }
  };

  // Handle quick range selection
  const handleQuickRange = (rangeValue) => {
    const today = new Date();
    let start, end;

    switch (rangeValue) {
      case 'today':
        start = new Date(today);
        end = new Date(today);
        break;
      case 'tomorrow':
        start = new Date(today);
        start.setDate(today.getDate() + 1);
        end = new Date(start);
        break;
      case 'thisWeek':
        start = new Date(today);
        const dayOfWeek = today.getDay();
        const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
        start.setDate(today.getDate() + mondayOffset);
        end = new Date(start);
        end.setDate(start.getDate() + 6);
        break;
      case 'thisMonth':
        start = new Date(today.getFullYear(), today.getMonth(), 1);
        end = new Date(today.getFullYear(), today.getMonth() + 1, 0);
        break;
      case 'thisYear':
        start = new Date(today.getFullYear(), 0, 1);
        end = new Date(today.getFullYear(), 11, 31);
        break;
      default:
        return;
    }

    setSelectedStartDate(start);
    setSelectedEndDate(end);
    setIsSelectingEndDate(false);
    setSelectedQuickRange(rangeValue);
  };

  // Handle save
  const handleSave = () => {
    if (selectedStartDate && selectedEndDate && onChange) {
      onChange({
        startDate: selectedStartDate,
        endDate: selectedEndDate
      });
    }
  };

  // Handle cancel
  const handleCancel = () => {
    setSelectedStartDate(startDate);
    setSelectedEndDate(endDate);
    setIsSelectingEndDate(false);
    if (onClose) onClose();
  };

  // Navigate months
  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  const renderMonth = (monthOffset = 0) => {
    const displayMonth = new Date(currentMonth);
    displayMonth.setMonth(currentMonth.getMonth() + monthOffset);
    
    const days = getCalendarDays(displayMonth);
    const monthName = displayMonth.toLocaleDateString('en-GB', {
      month: 'short',
      year: 'numeric'
    });

    return (
      <Box sx={{ flex: 1 }}>
        {/* Month Header */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          mb: 2
        }}>
          {monthOffset === 0 && (
            <IconButton onClick={() => navigateMonth(-1)} size="small">
              <ChevronLeftIcon />
            </IconButton>
          )}
          <Typography variant="h6" fontWeight="500" sx={{ flex: 1, textAlign: 'center' }}>
            {monthName}
          </Typography>
          {monthOffset === 1 && (
            <IconButton onClick={() => navigateMonth(1)} size="small">
              <ChevronRightIcon />
            </IconButton>
          )}
        </Box>

        {/* Weekday Headers */}
        <Grid container spacing={0} sx={{ mb: 1 }}>
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, index) => (
            <Grid xs key={`day-header-${index}`} sx={{ width: '14.28%' }}>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                py: 1,
                fontSize: '0.75rem',
                fontWeight: 500,
                color: 'text.secondary'
              }}>
                {day}
              </Box>
            </Grid>
          ))}
        </Grid>

        {/* Calendar Days */}
        <Grid container spacing={0}>
          {days.map((day, index) => {
            if (!day) {
              // Empty cell for days outside current month
              return (
                <Grid xs key={index} sx={{ width: '14.28%' }}>
                  <Box sx={{ height: 36, width: 36, margin: '2px auto' }} />
                </Grid>
              );
            }

            const isSelected = isDateSelected(day);
            const isStart = isStartDate(day);
            const isEnd = isEndDate(day);
            const isToday = day.toDateString() === new Date().toDateString();
            const inRange = isInRange(day);

            return (
              <Grid xs key={index} sx={{ width: '14.28%' }}>
                <Box
                  onClick={() => handleDateClick(day)}
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: 40,
                    width: 40,
                    margin: '1px auto',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    position: 'relative',
                    backgroundColor: 
                      isStart || isEnd ? theme.palette.primary.main :
                      inRange ? alpha(theme.palette.primary.main, 0.12) :
                      'transparent',
                    color: 
                      isStart || isEnd ? theme.palette.primary.contrastText :
                      inRange ? theme.palette.primary.main :
                      isToday ? theme.palette.primary.main :
                      theme.palette.text.primary,
                    fontWeight: 
                      isStart || isEnd ? 700 :
                      isToday ? 600 : 
                      inRange ? 500 : 
                      400,
                    border: isToday && !isSelected && !isStart && !isEnd ? `2px solid ${theme.palette.primary.main}` : 
                           isToday && (isStart || isEnd) ? `2px solid ${theme.palette.primary.contrastText}` : 'none',
                    boxShadow: isStart || isEnd ? `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}` : 'none',
                    transition: 'all 0.15s ease-in-out',
                    '&:hover': {
                      backgroundColor: 
                        isStart || isEnd ? theme.palette.primary.dark :
                        alpha(theme.palette.primary.main, 0.1),
                      transform: 'scale(1.05)',
                      boxShadow: isStart || isEnd ? 
                        `0 6px 16px ${alpha(theme.palette.primary.main, 0.4)}` : 
                        `0 2px 8px ${alpha(theme.palette.action.hover, 0.3)}`
                    }
                  }}
                >
                  {day.getDate()}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    );
  };

  return (
    <>
      {/* Main Content - Scrollable */}
      <Box sx={{ 
        p: { xs: 2, md: 3 }, 
        width: '100%',
        overflow: 'auto',
        flex: 1
      }}>
        <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
          {/* Quick Range Panel */}
          <Box sx={{ flexShrink: 0 }}>
            <Typography variant="subtitle2" fontWeight="500" sx={{ mb: 2 }}>
              Quick Range
            </Typography>
            
            {/* Desktop: List Layout */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <List dense sx={{ p: 0 }}>
                {quickRanges.map((range) => {
                  const isSelected = selectedQuickRange === range.value;
                  return (
                    <ListItem key={range.value} disablePadding>
                      <ListItemButton
                        onClick={() => handleQuickRange(range.value)}
                        sx={{
                          borderRadius: 1,
                          mb: 0.5,
                          py: 0.5,
                          backgroundColor: isSelected ? 'primary.main' : 'transparent',
                          color: isSelected ? 'primary.contrastText' : 'text.primary',
                          '&:hover': {
                            backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                          },
                          transition: 'all 0.2s ease-in-out'
                        }}
                      >
                        <ListItemText 
                          primary={range.label}
                          primaryTypographyProps={{
                            fontSize: '0.8rem',
                            fontWeight: isSelected ? 600 : 400
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            {/* Mobile: Chips Layout */}
            <Box sx={{ 
              display: { xs: 'flex', md: 'none' },
              flexWrap: 'wrap',
              gap: 1
            }}>
              {quickRanges.map((range) => {
                const isSelected = selectedQuickRange === range.value;
                return (
                  <Chip
                    key={range.value}
                    label={range.label}
                    onClick={() => handleQuickRange(range.value)}
                    variant={isSelected ? 'filled' : 'outlined'}
                    color={isSelected ? 'primary' : 'default'}
                    sx={{
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      fontWeight: isSelected ? 600 : 400,
                      '&:hover': {
                        backgroundColor: isSelected ? 'primary.dark' : 'action.hover'
                      },
                      transition: 'all 0.2s ease-in-out'
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Calendar */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ 
              display: 'flex', 
              gap: { xs: 1, md: 2 },
              flexDirection: { xs: 'column', sm: 'row' }
            }}>
              <Box sx={{ flex: 1 }}>
                {renderMonth(0)}
              </Box>
              <Box sx={{ flex: 1 }}>
                {renderMonth(1)}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Actions - Fixed at bottom */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end',
        gap: 2,
        p: { xs: 2, md: 3 },
        borderTop: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        backgroundColor: 'background.paper'
      }}>
        <Button 
          variant="outlined" 
          onClick={handleCancel}
          sx={{
            borderRadius: 2,
            textTransform: 'none'
          }}
        >
          Cancel
        </Button>
        <Button 
          variant="contained" 
          onClick={handleSave}
          disabled={!selectedStartDate || !selectedEndDate}
          sx={{
            borderRadius: 2,
            textTransform: 'none'
          }}
        >
          Apply
        </Button>
      </Box>
    </>
  );
};

export default DateRangeCalendar;